import type { Service, ServiceContent } from '@/base-api/types';
import { useState } from 'preact/hooks';
import { useDialog } from '@/contexts/dialog-context';
import { useAddOfferToCart } from '@/hooks/useAddOfferToCart';
import { formatCurrency } from '@/utils/number';

import { Button } from './button';

interface OfferModalInput {
  content: ServiceContent['offer_modal'];
  service: Service;
  cartId: string;
  variantId: number;
  reloadOnAddOfferToCart?: boolean;
}

const getMostCostlyOffering = (offerings: Service['offerings']) => {
  if (offerings.length === 0) return null;

  return offerings.reduce((prev, curr) =>
    curr.price > prev.price ? curr : prev,
  );
};

export const OfferModalInner = (props: OfferModalInput) => {
  const { content, service, cartId, variantId, reloadOnAddOfferToCart } = props;

  const [activeOffer, setActiveOffer] = useState<number | null>(() => {
    const mostCostly = getMostCostlyOffering(service.offerings);
    return mostCostly ? mostCostly.id : null;
  });

  const { closeDialog } = useDialog();

  const { isLoading, onAddOfferToCart } = useAddOfferToCart({
    cartId,
    variantId,
  });

  const {
    heading_image,
    heading,
    bullet_heading,
    bullet_1,
    bullet_2,
    bullet_3,
    bullet_4,
    bullet_5,
    terms_url,
    terms_label,
    background_image,
    accept_label,
    decline_label,
  } = content;

  const handleCloseDialog = () => {
    closeDialog();
  };

  const handleAddToCart = async () => {
    if (activeOffer) {
      await onAddOfferToCart(activeOffer);
    }

    if (reloadOnAddOfferToCart) {
      window.location.reload();
    }

    closeDialog();
  };

  return (
    <div className="services-modal__content services-modal__content--offer">
      <div className="services-modal__container">
        {heading_image && (
          <div className="services-modal__heading-image">
            <img src={heading_image} alt="Offer Heading Illustration" />
          </div>
        )}
        <div className="services-modal__info">
          {heading && <p className="services-modal__heading h4">{heading}</p>}

          {bullet_heading && (
            <span className="services-modal__bullet-heading">
              {bullet_heading}
            </span>
          )}
          {(bullet_1 || bullet_2 || bullet_3 || bullet_4 || bullet_5) && (
            <ul className="services-modal__bullets">
              {bullet_1 && <li>{bullet_1}</li>}
              {bullet_2 && <li>{bullet_2}</li>}
              {bullet_3 && <li>{bullet_3}</li>}
              {bullet_4 && <li>{bullet_4}</li>}
              {bullet_5 && <li>{bullet_5}</li>}
            </ul>
          )}

          {terms_url && terms_label && (
            <p className="services-modal__terms">
              <a href={terms_url} target="_blank" rel="noopener noreferrer">
                {terms_label}
              </a>
            </p>
          )}
        </div>
        <hr />
        <div className="services-modal__lower">
          <div className="services-modal__offers">
            {service.offerings.map((offer) => {
              return (
                <Button
                  key={offer.id}
                  size="xl"
                  active={activeOffer === offer.id}
                  onClick={() => setActiveOffer(offer.id)}
                >
                  <div>
                    <span className="services-modal__offer-name">
                      {offer.name}
                    </span>
                    <span className="services-modal__offer-divider">
                      Protection
                    </span>
                    <strong className="services-modal__offer-price">
                      {formatCurrency(offer.price)}
                    </strong>
                  </div>
                </Button>
              );
            })}
          </div>
          <div className="services-modal__actions">
            <div className="button-group">
              <Button variant="ghost" size="lg" onClick={handleCloseDialog}>
                {decline_label ? decline_label : 'No Thanks'}
              </Button>
              <Button
                variant="primary"
                size="lg"
                disabled={isLoading || activeOffer === null}
                onClick={handleAddToCart}
              >
                {isLoading
                  ? 'Adding to Cart...'
                  : accept_label || 'Accept Offer'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="services-modal__background-image">
        {background_image && (
          <img src={background_image} alt="Background Image" />
        )}
      </div>
    </div>
  );
};
